import { useRef } from "react";
import { cn } from "@/lib/cn";
import { latestMessageScrollKey, useAutoScrollToLatest } from "@/hooks/use-auto-scroll-to-latest";
import { Icons } from "@/components/ui/icons";
import { activityEntryLabel } from "@/lib/work-toast-tool-labels";
import type { WorkToastActivityEntry } from "@/stores/work-toast-store";
import { ToastActionButton, type ToastPillAction } from "./toast-pill";
import { workToastActionIcon } from "./work-toast-action-icon";
import { WorkToastTextPreview } from "./work-toast-text-preview";

/** Last three run events, oldest first, with the current event emphasized. */
export function WorkToastActivityLog({
  entries,
  action,
}: {
  entries: WorkToastActivityEntry[];
  action?: ToastPillAction;
}) {
  const rows = entries.slice(-3);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollKey = latestMessageScrollKey(rows.map((entry) => ({
    id: entry.id,
    role: entry.kind,
    content: entry.kind === "text" ? entry.textFull ?? entry.text ?? "" : activityEntryLabel(entry),
  })));
  const { onScroll } = useAutoScrollToLatest(scrollRef, scrollKey);
  if (rows.length === 0) return null;
  return (
    <>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex max-h-60 min-w-0 flex-col gap-1 overflow-y-auto overscroll-contain"
        data-testid="work-toast-activity-log"
      >
        {rows.map((entry, index) => {
          const Icon = entry.kind === "computer"
            ? Icons[workToastActionIcon(entry.action?.toLowerCase().replace(/\s+/g, "-") ?? "")]
            : entry.kind === "tool" ? Icons.Wrench : null;
          return <div
            key={entry.id}
            className={cn(
              "min-w-0 text-appkit-small",
              entry.kind === "text" ? "break-words" : "flex items-center gap-1 truncate",
              index === rows.length - 1 ? "text-primary" : "text-muted",
            )}
            data-testid={`work-toast-activity-row-${index}`}
          >
            {Icon && <Icon className="h-3 w-3 shrink-0" aria-hidden="true" data-testid={`work-toast-activity-icon-${index}`} />}
            {entry.kind === "text" ? (
              <WorkToastTextPreview
                key={rows[rows.length - 1]?.id}
                text={entry.textFull ?? entry.text ?? ""}
                compact={index < rows.length - 1}
              />
            ) : <span className="truncate">{activityEntryLabel(entry)}</span>}
          </div>;
        })}
      </div>
      {action && (
        <div className="flex justify-end">
          <ToastActionButton action={action} />
        </div>
      )}
    </>
  );
}
